export interface CoachingReportTab {
  key: string;
  label: string;
  body: string;
}

const TAB_KEYS: ReadonlyArray<{ key: string; label: string }> = [
  { key: "coachingNotes", label: "Weekly Coaching Notes" },
  { key: "coachingRationale", label: "Coaching Rationale" }
];

const humaniseKey = (key: string): string =>
  key
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/^./, (c) => c.toUpperCase());

const buildLegacyTabs = (sections: unknown): CoachingReportTab[] => {
  if (!Array.isArray(sections)) {
    return [];
  }
  const entries = sections
    .map((raw, index) => {
      if (!raw || typeof raw !== "object") {
        return null;
      }
      const section = raw as Record<string, unknown>;
      const body = typeof section.content === "string" ? section.content : "";
      if (body.length === 0) {
        return null;
      }
      const rawLabel = [section.T, section.name, section.title].find(
        (v) => typeof v === "string" && v.length > 0
      ) as string | undefined;
      const label = rawLabel ?? `Section ${index + 1}`;
      const order = typeof section.D_O === "number" ? section.D_O : Number.POSITIVE_INFINITY;
      return { key: `legacy-${index}`, label, body, order };
    })
    .filter((tab): tab is { key: string; label: string; body: string; order: number } => tab !== null);
  entries.sort((a, b) => a.order - b.order);
  return entries.map(({ key, label, body }) => ({ key, label, body }));
};

export const buildReportTabs = (content: unknown): CoachingReportTab[] => {
  if (!content || typeof content !== "object") {
    return [];
  }
  const record = content as Record<string, unknown>;

  const legacyTabs = buildLegacyTabs(record.sections);
  if (legacyTabs.length > 0) {
    return legacyTabs;
  }

  const stringEntries = Object.entries(record).filter(
    ([key, value]) => key !== "title" && typeof value === "string" && value.length > 0
  ) as [string, string][];
  const entryMap = new Map(stringEntries);

  const knownTabs = TAB_KEYS
    .filter(({ key }) => entryMap.has(key))
    .map(({ key, label }) => ({ key, label, body: entryMap.get(key) as string }));

  const knownKeySet = new Set(TAB_KEYS.map((t) => t.key));
  const unknownTabs = stringEntries
    .filter(([key]) => !knownKeySet.has(key))
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, body]) => ({ key, label: humaniseKey(key), body }));

  return [...knownTabs, ...unknownTabs];
};
